/**
 * ========================================
 * JOURNEY STEP CARDS - INDIVIDUAL COMPONENTS
 * ========================================
 * 
 * This file contains individual card components for each journey step.
 * NO LOOPS - Each card is separate so you can manually adjust positions individually.
 * 
 * TO ADJUST POSITIONS:
 * - Change the 'top' values in each component (Desktop, Tablet, Mobile)
 * - Change 'left' or 'right' percentages for horizontal positioning
 * - All pixel values are clearly visible and easy to modify
 */

import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";

// Import images
import amico1 from "./assets/amico1.png";
import smartReport from "./assets/Group 1261153177.png";
import bodySummary from "./assets/Group 1261153136.png";
import chatbot from "./assets/Group 1261153170.png";
import targetedMarketing from "./assets/Gemini_Generated_Image_sa837ssa837ssa83.png";

// ============ STEP 1 CARD ============
export const JourneyStepCard1 = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Additional breakpoint for very small mobile devices (<400px)
  const isVerySmallMobile = useMediaQuery("(max-width:390px)");
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Scroll reveal animation - detects when card enters/leaves viewport (works every scroll)
  // Mobile: No animation - cards are always visible
  // Desktop/Tablet: Animated reveal on scroll
  useEffect(() => {
    // On mobile, show cards immediately without animation
    if (isMobile) {
      setIsVisible(true);
      return;
    }

    // Desktop/Tablet: Scroll-based reveal animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Set visible when entering, invisible when leaving (allows animation to repeat)
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 } // Trigger when 10% of card is visible
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [isMobile]);

  // POSITION VALUES - Adjust these to align with circle 1 on the curve
  const topDesktop = 100; // Desktop: Change this value (in pixels)
  const topTablet = 140;  // Tablet: Interpolated between mobile (50) and desktop (100)
  const topMobile = isVerySmallMobile ? 45 : 50;   // Mobile: Smaller top for very small screens
  const topMobileLarge = 55;   // Mobile (400-850px): Standard position
  
  const topValue = isMobile ? (isVerySmallMobile ? topMobile : topMobileLarge) : isSmallScreen ? topTablet : topDesktop;
  const leftDesktop = "32%"; // Desktop: Change this to move left/right
  const leftTablet = "18%";   // Tablet: Interpolated between mobile (16%) and desktop (29%)
  const leftMobile = isVerySmallMobile ? "14%" : "16%";   // Mobile: Closer to edge on very small screens
  const leftValue = isMobile ? leftMobile : isSmallScreen ? leftTablet : leftDesktop;

  return (
    <Box
      ref={cardRef}
      sx={{
        position: "absolute",
        // TOP POSITION - Clearly visible pixel values
        // Desktop: 100px | Tablet: 75px | Mobile: 50px
        // TO ADJUST: Change topDesktop, topTablet, or topMobile variables above
        top: `${topValue}px`,
        // LEFT POSITION
        // Desktop: 29% | Tablet: 9.5% | Mobile: 16%
        // TO ADJUST: Change leftDesktop, leftTablet, or leftMobile variables above
        left: leftValue,
        // CARD WIDTH - Responsive for all mobile sizes
        width: isVerySmallMobile ? "89%" : isMobile ? "88%" : isSmallScreen ? "7  5%" : "50%",
        display: "flex",
        flexDirection: isMobile ? "row" : isSmallScreen ? "row" : "row", // TODO: Add tablet value if needed
        alignItems: isMobile ? "flex-start" : isSmallScreen ? "center" : "center", // TODO: Add tablet value if needed
        gap: isVerySmallMobile ? "8px" : isMobile ? "10px" : isSmallScreen ? "16px" : "32px",
        zIndex: 2,
        // Scroll reveal animation - fades in and slides up when card enters viewport
        // Mobile: No animation (always visible)
        // Desktop/Tablet: Animated reveal
        opacity: isMobile ? 1 : (isVisible ? 1 : 0),
        transform: isMobile ? "translateY(0)" : (isVisible ? "translateY(0)" : "translateY(30px)"),
        transition: isMobile ? "none" : "opacity 0.6s ease-out, transform 0.6s ease-out",
      }}
    >
      {/* Image */}
      <Box
        component="img"
        src={amico1}
        alt="Branding essential"
        sx={{
          width: isVerySmallMobile ? "75px" : isMobile ? "90px" : isSmallScreen ? "140px" : "200px",
          height: isVerySmallMobile ? "75px" : isMobile ? "90px" : isSmallScreen ? "140px" : "200px",
          objectFit: "contain",
          flexShrink: 0,
          marginTop: isVerySmallMobile ? "15px" : isMobile ? "22px" : isSmallScreen ? "0px" : "0px", // TODO: Add tablet value if needed
        }}
      />

      {/* Content Card */}
      <Box
        sx={{
          backgroundColor: "transparent",
          borderRadius: isMobile ? "12px" : isSmallScreen ? "14px" : "16px", // TODO: Add tablet value if needed
          padding: isMobile ? "14px" : isSmallScreen ? "18px" : "24px",
          flex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: isVerySmallMobile ? "11px" : isMobile ? "13px" : isSmallScreen ? "18px" : "24px",
            fontWeight: 700,
            fontFamily: "'Poppins', sans-serif",
            color: "#1D47A4",
            marginBottom: isMobile ? "6px" : isSmallScreen ? "7px" : "8px", // TODO: Add tablet value if needed
            lineHeight: 1.3,
          }}
        >
          Branding essential (Reporting)
        </Typography>
        <Typography
          sx={{
            fontSize: isVerySmallMobile ? "8px" : isMobile ? "9px" : isSmallScreen ? "12px" : "14px",
            lineHeight: 1.2,
            color: "#4B5563",
            marginBottom: "16px",
          }}
        >
          Transform how your PDF reports are read by users. Smart PDF reports keeps you ahead of the competition and registers your brand
        </Typography>
        <Button
          variant="contained"
          sx={{
            padding: isMobile ? "5px 5px 5px 10px" : isSmallScreen ? "6px 6px 6px 12px" : "8px 8px 8px 16px",
            backgroundColor: "#3567D6",
            textTransform: "none",
            fontSize: isMobile ? "10px" : isSmallScreen ? "11px" : "14px",
            fontWeight: 600,
            borderRadius: isMobile ? "6px" : isSmallScreen ? "7px" : "8px", // TODO: Add tablet value if needed
            "&:hover": {
              backgroundColor: "#2557C6",
            },
          }}
          onClick={() => navigate("/smartReport")}
        >
          View Smart Report
          <ArrowForwardIcon
            sx={{
              borderRadius: isMobile ? "3px" : isSmallScreen ? "4px" : "8px",
              background: "white",
              fill: "#3567D6",
              width: isMobile ? "16px" : isSmallScreen ? "18px" : "32px",
              height: isMobile ? "16px" : isSmallScreen ? "18px" : "32px",
              marginLeft: isMobile ? "5px" : isSmallScreen ? "6px" : "8px",
            }}
          />
        </Button>
      </Box>
    </Box>
  );
};

// ============ STEP 2 CARD ============
export const JourneyStepCard2 = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isVerySmallMobile = useMediaQuery("(max-width:390px)");
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Scroll reveal animation - detects when card enters/leaves viewport (works every scroll)
  // Mobile: No animation - cards are always visible
  // Desktop/Tablet: Animated reveal on scroll
  useEffect(() => {
    // On mobile, show cards immediately without animation
    if (isMobile) {
      setIsVisible(true);
      return;
    }

    // Desktop/Tablet: Scroll-based reveal animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Set visible when entering, invisible when leaving (allows animation to repeat)
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 } // Trigger when 10% of card is visible
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [isMobile]);

  // POSITION VALUES - Adjust these to align with circle 2 on the curve
  const topDesktop = 420; // Desktop: Change this value (in pixels)
  const topTablet = 450; // Tablet: Interpolated between mobile (190) and desktop (400)
  const topMobile = isVerySmallMobile ? 200 : 210; // Mobile: Smaller for very small screens
  const topMobileLarge = 210; // Mobile (400-850px): Standard position
  
  const topValue = isMobile ? (isVerySmallMobile ? topMobile : topMobileLarge) : isSmallScreen ? topTablet : topDesktop;
  const rightDesktop = "30%";  // Desktop: Change this to move left/right
  const rightTablet = "22%";   // Tablet: Interpolated between mobile (13%) and desktop (30%)
  const rightMobile = isVerySmallMobile ? "12%" : "13%";   // Mobile: Closer to edge on very small screens
  const rightValue = isMobile ? rightMobile : isSmallScreen ? rightTablet : rightDesktop;

  return (
    <Box
      ref={cardRef}
      sx={{
        position: "absolute",
        // TOP POSITION - Clearly visible pixel values
        // Desktop: 400px | Tablet: 295px | Mobile: 190px
        // TO ADJUST: Change topDesktop, topTablet, or topMobile variables above
        top: `${topValue}px`,
        // RIGHT POSITION
        // Desktop: 30% | Tablet: 21.5% | Mobile: 13%
        // TO ADJUST: Change rightDesktop, rightTablet, or rightMobile variables above
        right: rightValue,
        // CARD WIDTH - Responsive for all mobile sizes
        width: isVerySmallMobile ? "88%" : isMobile ? "88%" : isSmallScreen ? "75%" : "50%",
        display: "flex",
        flexDirection: isMobile ? "row-reverse" : isSmallScreen ? "row-reverse" : "row-reverse", // TODO: Add tablet value if needed
        alignItems: isMobile ? "flex-start" : isSmallScreen ? "center" : "center", // TODO: Add tablet value if needed
        gap: isVerySmallMobile ? "4px" : isMobile ? "0px" : isSmallScreen ? "16px" : "29px",
        zIndex: 2,
        // Scroll reveal animation - fades in and slides up when card enters viewport
        // Mobile: No animation (always visible)
        // Desktop/Tablet: Animated reveal
        opacity: isMobile ? 1 : (isVisible ? 1 : 0),
        transform: isMobile ? "translateY(0)" : (isVisible ? "translateY(0)" : "translateY(30px)"),
        transition: isMobile ? "none" : "opacity 0.6s ease-out, transform 0.6s ease-out",
      }}
    >
      {/* Image */}
      <Box
        component="img"
        src={smartReport}
        alt="Retention boost"
        sx={{
          width: isVerySmallMobile ? "70px" : isMobile ? "90px" : isSmallScreen ? "140px" : "260px",
          height: isVerySmallMobile ? "70px" : isMobile ? "90px" : isSmallScreen ? "140px" : "260px",
          objectFit: "contain",
          flexShrink: 0,
          marginTop: isMobile ? "25px" : isSmallScreen ? "20px" : "20px", // TODO: Add tablet value if needed
        }}
      />

      {/* Content Card */}
      <Box
        sx={{
          backgroundColor: "transparent",
          borderRadius: isMobile ? "12px" : isSmallScreen ? "14px" : "16px", // TODO: Add tablet value if needed
          padding: isMobile ? "14px" : isSmallScreen ? "18px" : "24px",
          flex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: isVerySmallMobile ? "11px" : isMobile ? "13px" : isSmallScreen ? "18px" : "24px",
            fontWeight: 700,
            fontFamily: "'Poppins', sans-serif",
            color: "#1D47A4",
            marginBottom: isMobile ? "6px" : isSmallScreen ? "7px" : "8px", // TODO: Add tablet value if needed
            lineHeight: 1.3,
          }}
        >
          Retention boost (Upsell, repeat rate)
        </Typography>
        <Typography
          sx={{
            fontSize: isVerySmallMobile ? "8px" : isMobile ? "9px" : isSmallScreen ? "12px" : "14px",
            lineHeight: 1.2,
            color: "#4B5563",
            marginBottom: "16px",
          }}
        >
          Go for the add on features like test recommendation, trend analysis, risk score update to make your brand a natural choice for the next purchase by the user
        </Typography>
        <Button
          variant="contained"
          sx={{
            padding: isMobile ? "5px 5px 5px 10px" : isSmallScreen ? "6px 6px 6px 12px" : "8px 8px 8px 16px",
            backgroundColor: "#3567D6",
            textTransform: "none",
            fontSize: isMobile ? "10px" : isSmallScreen ? "11px" : "14px",
            fontWeight: 600,
            borderRadius: isMobile ? "6px" : isSmallScreen ? "7px" : "8px", // TODO: Add tablet value if needed
            "&:hover": {
              backgroundColor: "#2557C6",
            },
          }}
          onClick={() => navigate("/smartReport")}
        >
          View Smart Report
          <ArrowForwardIcon
            sx={{
              borderRadius: isMobile ? "3px" : isSmallScreen ? "4px" : "8px",
              background: "white",
              fill: "#3567D6",
              width: isMobile ? "16px" : isSmallScreen ? "18px" : "32px",
              height: isMobile ? "16px" : isSmallScreen ? "18px" : "32px",
              marginLeft: isMobile ? "5px" : isSmallScreen ? "6px" : "8px",
            }}
          />
        </Button>
      </Box>
    </Box>
  );
};

// ============ STEP 3 CARD ============
export const JourneyStepCard3 = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isVerySmallMobile = useMediaQuery("(max-width:390px)");
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Scroll reveal animation - detects when card enters/leaves viewport (works every scroll)
  // Mobile: No animation - cards are always visible
  // Desktop/Tablet: Animated reveal on scroll
  useEffect(() => {
    // On mobile, show cards immediately without animation
    if (isMobile) {
      setIsVisible(true);
      return;
    }

    // Desktop/Tablet: Scroll-based reveal animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Set visible when entering, invisible when leaving (allows animation to repeat)
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 } // Trigger when 10% of card is visible
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [isMobile]);

  // POSITION VALUES - Adjust these to align with circle 3 on the curve
  const topDesktop = 790; // Desktop: Change this value (in pixels)
  const topTablet = 775;   // Tablet: Interpolated between mobile (360) and desktop (790)
  const topMobile = isVerySmallMobile ? 360 : 360;    // Mobile: Smaller for very small screens
  const topMobileLarge = 380;    // Mobile (400-850px): Standard position
  
  const topValue = isMobile ? (isVerySmallMobile ? topMobile : topMobileLarge) : isSmallScreen ? topTablet : topDesktop;
  const leftDesktop = "29%"; // Desktop: Change this to move left/right
  const leftTablet = "19%";   // Tablet: Interpolated between mobile (16%) and desktop (29%)
  const leftMobile = isVerySmallMobile ? "10%" : "16%";   // Mobile: Closer to edge on very small screens
  const leftValue = isMobile ? leftMobile : isSmallScreen ? leftTablet : leftDesktop;

  return (
    <Box
      ref={cardRef}
      sx={{
        position: "absolute",
        // TOP POSITION - Clearly visible pixel values
        // Desktop: 790px | Tablet: 575px | Mobile: 360px
        // TO ADJUST: Change topDesktop, topTablet, or topMobile variables above
        top: `${topValue}px`,
        // LEFT POSITION
        // Desktop: 29% | Tablet: 22.5% | Mobile: 16%
        // TO ADJUST: Change leftDesktop, leftTablet, or leftMobile variables above
        left: leftValue,
        // CARD WIDTH - Responsive for all mobile sizes
        width: isVerySmallMobile ? "88%" : isMobile ? "88%" : isSmallScreen ? "75%" : "55%",
        display: "flex",
        flexDirection: isMobile ? "row" : "row",
        alignItems: isMobile ? "flex-start" : "center",
        gap: isVerySmallMobile ? "6px" : isMobile ? "8px" : isSmallScreen ? "16px" : "32px",
        zIndex: 2,
        // Scroll reveal animation - fades in and slides up when card enters viewport
        // Mobile: No animation (always visible)
        // Desktop/Tablet: Animated reveal
        opacity: isMobile ? 1 : (isVisible ? 1 : 0),
        transform: isMobile ? "translateY(0)" : (isVisible ? "translateY(0)" : "translateY(30px)"),
        transition: isMobile ? "none" : "opacity 0.6s ease-out, transform 0.6s ease-out",
      }}
    >
      {/* Image */}
      <Box
        component="img"
        src={bodySummary}
        alt="Go Digital"
        sx={{
          width: isVerySmallMobile ? "70px" : isMobile ? "90px" : isSmallScreen ? "140px" : "220px",
          height: isVerySmallMobile ? "70px" : isMobile ? "90px" : isSmallScreen ? "140px" : "220px",
          objectFit: "contain",
          flexShrink: 0,
          marginTop: isMobile ? "25px" : isSmallScreen ? "0px" : "0px", // TODO: Add tablet value if needed
        }}
      />

      {/* Content Card */}
      <Box
        sx={{
          backgroundColor: "transparent",
          borderRadius: isMobile ? "12px" : isSmallScreen ? "14px" : "16px", // TODO: Add tablet value if needed
          padding: isMobile ? "14px" : isSmallScreen ? "18px" : "24px",
          flex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: isVerySmallMobile ? "11px" : isMobile ? "13px" : isSmallScreen ? "18px" : "24px",
            fontWeight: 700,
            fontFamily: "'Poppins', sans-serif",
            color: "#1D47A4",
            marginBottom: isMobile ? "6px" : isSmallScreen ? "7px" : "8px", // TODO: Add tablet value if needed
            lineHeight: 1.3,
          }}
        >
          Go Digital (Actionable)
        </Typography>
        <Typography
          sx={{
            fontSize: isVerySmallMobile ? "8px" : isMobile ? "9px" : isSmallScreen ? "12px" : "14px",
            lineHeight: 1.2,
            color: "#4B5563",
            marginBottom: "16px",
          }}
        >
          Make the whole experience more interactive and user friendly with viz app, educational videos. Increase your chances of user interaction by 3x as compared to a PDF
        </Typography>
        <Button
          variant="contained"
          sx={{
            padding: isMobile ? "5px 5px 5px 10px" : isSmallScreen ? "6px 6px 6px 12px" : "8px 8px 8px 16px",
            backgroundColor: "#3567D6",
            textTransform: "none",
            fontSize: isMobile ? "10px" : isSmallScreen ? "11px" : "14px",
            fontWeight: 600,
            borderRadius: isMobile ? "6px" : isSmallScreen ? "7px" : "8px", // TODO: Add tablet value if needed
            "&:hover": {
              backgroundColor: "#2557C6",
            },
          }}
          onClick={() => navigate("/vizapp")}
        >
          View Viz App
          <ArrowForwardIcon
            sx={{
              borderRadius: isMobile ? "3px" : isSmallScreen ? "4px" : "8px",
              background: "white",
              fill: "#3567D6",
              width: isMobile ? "16px" : isSmallScreen ? "18px" : "32px",
              height: isMobile ? "16px" : isSmallScreen ? "18px" : "32px",
              marginLeft: isMobile ? "5px" : isSmallScreen ? "6px" : "8px",
            }}
          />
        </Button>
      </Box>
    </Box>
  );
};

// ============ STEP 4 CARD ============
export const JourneyStepCard4 = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isVerySmallMobile = useMediaQuery("(max-width:385px)");
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Scroll reveal animation - detects when card enters/leaves viewport (works every scroll)
  // Mobile: No animation - cards are always visible
  // Desktop/Tablet: Animated reveal on scroll
  useEffect(() => {
    // On mobile, show cards immediately without animation
    if (isMobile) {
      setIsVisible(true);
      return;
    }

    // Desktop/Tablet: Scroll-based reveal animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Set visible when entering, invisible when leaving (allows animation to repeat)
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 } // Trigger when 10% of card is visible
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [isMobile]);

  // POSITION VALUES - Adjust these to align with circle 4 on the curve
  const topDesktop = 1120; // Desktop: Change this value (in pixels)
  const topTablet = 1120;  // Tablet: Interpolated between mobile (520) and desktop (1100)
  const topMobile = isVerySmallMobile ? 510 : 560;  // Mobile: Smaller for very small screens
  const topMobileLarge = 560;  // Mobile (400-850px): Standard position
  
  const topValue = isMobile ? (isVerySmallMobile ? topMobile : topMobileLarge) : isSmallScreen ? topTablet : topDesktop;
  const rightDesktop = "30%";  // Desktop: Change this to move left/right
  const rightTablet = "18%";   // Tablet: Interpolated between mobile (13%) and desktop (30%)
  const rightMobile = isVerySmallMobile ? "12%" : "13%";   // Mobile: Closer to edge on very small screens
  const rightValue = isMobile ? rightMobile : isSmallScreen ? rightTablet : rightDesktop;

  return (
    <Box
      ref={cardRef}
      sx={{
        position: "absolute",
        // TOP POSITION - Clearly visible pixel values
        // Desktop: 1100px | Tablet: 810px | Mobile: 520px
        // TO ADJUST: Change topDesktop, topTablet, or topMobile variables above
        top: `${topValue}px`,
        // RIGHT POSITION
        // Desktop: 30% | Tablet: 21.5% | Mobile: 13%
        // TO ADJUST: Change rightDesktop, rightTablet, or rightMobile variables above
        right: rightValue,
        // CARD WIDTH
        width: isVerySmallMobile ? "88%" : isMobile ? "88%" : isSmallScreen ? "75%" : "52%",
        display: "flex",
        flexDirection: isMobile ? "row-reverse" : isSmallScreen ? "row-reverse" : "row-reverse", // TODO: Add tablet value if needed
        alignItems: isMobile ? "flex-start" : isSmallScreen ? "center" : "center", // TODO: Add tablet value if needed
        gap: isMobile ? "0px" : isSmallScreen ? "0px" : "22px",
        zIndex: 2,
        // Scroll reveal animation - fades in and slides up when card enters viewport
        // Mobile: No animation (always visible)
        // Desktop/Tablet: Animated reveal
        opacity: isMobile ? 1 : (isVisible ? 1 : 0),
        transform: isMobile ? "translateY(0)" : (isVisible ? "translateY(0)" : "translateY(30px)"),
        transition: isMobile ? "none" : "opacity 0.6s ease-out, transform 0.6s ease-out",
      }}
    >
      {/* Image */}
      <Box
        component="img"
        src={chatbot}
        alt="Counselling"
        sx={{
          width: isVerySmallMobile ? "70px" : isMobile ? "90px" : isSmallScreen ? "140px" : "220px",
          height: isVerySmallMobile ? "70px" : isMobile ? "90px" : isSmallScreen ? "140px" : "220px",
          objectFit: "contain",
          flexShrink: 0,
          marginTop: isMobile ? "25px" : isSmallScreen ? "0px" : "0px", // TODO: Add tablet value if needed
        }}
      />

      {/* Content Card */}
      <Box
        sx={{
          backgroundColor: "transparent",
          borderRadius: isMobile ? "12px" : isSmallScreen ? "14px" : "16px", // TODO: Add tablet value if needed
          padding: isMobile ? "14px" : isSmallScreen ? "18px" : "24px",
          flex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: isVerySmallMobile ? "11px" : isMobile ? "13px" : isSmallScreen ? "18px" : "24px",
            fontWeight: 700,
            fontFamily: "'Poppins', sans-serif",
            color: "#1D47A4",
            marginBottom: isMobile ? "6px" : isSmallScreen ? "7px" : "8px", // TODO: Add tablet value if needed
            lineHeight: 1.3,
          }}
        >
          Counselling (Personalised communication)
        </Typography>
        <Typography
          sx={{
            fontSize: isVerySmallMobile ? "8px" : isMobile ? "9px" : isSmallScreen ? "12px" : "14px",
            lineHeight: 1.2,
            color: "#4B5563",
            marginBottom: "16px",
          }}
        >
          Hear your patients by answering their targeted queries through chatbot
        </Typography>
        <Button
          variant="contained"
          sx={{
            padding: isMobile ? "5px 5px 5px 10px" : isSmallScreen ? "6px 6px 6px 12px" : "8px 8px 8px 16px",
            backgroundColor: "#3567D6",
            textTransform: "none",
            fontSize: isMobile ? "10px" : isSmallScreen ? "11px" : "14px",
            fontWeight: 600,
            borderRadius: isMobile ? "6px" : isSmallScreen ? "7px" : "8px", // TODO: Add tablet value if needed
            "&:hover": {
              backgroundColor: "#2557C6",
            },
          }}
          onClick={() => navigate("/engagement")}
        >
          View Chat Bot
          <ArrowForwardIcon
            sx={{
              borderRadius: isMobile ? "3px" : isSmallScreen ? "4px" : "8px",
              background: "white",
              fill: "#3567D6",
              width: isMobile ? "16px" : isSmallScreen ? "18px" : "32px",
              height: isMobile ? "16px" : isSmallScreen ? "18px" : "32px",
              marginLeft: isMobile ? "5px" : isSmallScreen ? "6px" : "8px",
            }}
          />
        </Button>
      </Box>
    </Box>
  );
};

// ============ STEP 5 CARD ============
export const JourneyStepCard5 = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isVerySmallMobile = useMediaQuery("(max-width:385px)");
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Scroll reveal animation - detects when card enters/leaves viewport (works every scroll)
  // Mobile: No animation - cards are always visible
  // Desktop/Tablet: Animated reveal on scroll
  useEffect(() => {
    // On mobile, show cards immediately without animation
    if (isMobile) {
      setIsVisible(true);
      return;
    }

    // Desktop/Tablet: Scroll-based reveal animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Set visible when entering, invisible when leaving (allows animation to repeat)
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 } // Trigger when 10% of card is visible
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [isMobile]);

  // POSITION VALUES - Adjust these to align with circle 5 on the curve
  const topDesktop = 1460; // Desktop: Change this value (in pixels)
  const topTablet = 1400;  // Tablet: Interpolated between mobile (660) and desktop (1460)
  const topMobile = isVerySmallMobile ? 670 : 700;   // Mobile: Smaller for very small screens
  const topMobileLarge = 720;   // Mobile (400-850px): Standard position
  
  const topValue = isMobile ? (isVerySmallMobile ? topMobile : topMobileLarge) : isSmallScreen ? topTablet : topDesktop;
  const leftDesktop = "31%"; // Desktop: Change this to move left/right
  const leftTablet = "22.5%";   // Tablet: Interpolated between mobile (16%) and desktop (29%)
  const leftMobile = isVerySmallMobile ? "13%" : "16%";   // Mobile: Closer to edge on very small screens
  const leftValue = isMobile ? leftMobile : isSmallScreen ? leftTablet : leftDesktop;

  return (
    <Box
      ref={cardRef}
      sx={{
        position: "absolute",
        // TOP POSITION - Clearly visible pixel values
        // Desktop: 1460px | Tablet: 1060px | Mobile: 660px
        // TO ADJUST: Change topDesktop, topTablet, or topMobile variables above
        top: `${topValue}px`,
        // LEFT POSITION
        // Desktop: 29% | Tablet: 22.5% | Mobile: 16%
        // TO ADJUST: Change leftDesktop, leftTablet, or leftMobile variables above
        left: leftValue,
        // CARD WIDTH - Responsive for all mobile sizes
        width: isVerySmallMobile ? "88%" : isMobile ? "87%" : isSmallScreen ? "75%" : "50%",
        display: "flex",
        flexDirection: isMobile ? "row" : "row",
        alignItems: isMobile ? "flex-start" : "center",
        gap: isVerySmallMobile ? "6px" : isMobile ? "8px" : isSmallScreen ? "16px" : "32px",
        zIndex: 2,
        // Scroll reveal animation - fades in and slides up when card enters viewport
        // Mobile: No animation (always visible)
        // Desktop/Tablet: Animated reveal
        opacity: isMobile ? 1 : (isVisible ? 1 : 0),
        transform: isMobile ? "translateY(0)" : (isVisible ? "translateY(0)" : "translateY(30px)"),
        transition: isMobile ? "none" : "opacity 0.6s ease-out, transform 0.6s ease-out",
      }}
    >
      {/* Image */}
      <Box
        component="img"
        src={targetedMarketing}
        alt="Targeted marketing"
        sx={{
          width: isVerySmallMobile ? "70px" : isMobile ? "90px" : isSmallScreen ? "140px" : "250px",
          height: isVerySmallMobile ? "70px" : isMobile ? "90px" : isSmallScreen ? "140px" : "250px",
          objectFit: "contain",
          borderRadius: "10px",
          flexShrink: 0,
          marginTop: isMobile ? "25px" : isSmallScreen ? "0px" : "0px", // TODO: Add tablet value if needed
        }}
      />

      {/* Content Card */}
      <Box
        sx={{
          backgroundColor: "transparent",
          borderRadius: isMobile ? "12px" : isSmallScreen ? "14px" : "16px", // TODO: Add tablet value if needed
          padding: isMobile ? "14px" : isSmallScreen ? "18px" : "24px",
          flex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: isVerySmallMobile ? "11px" : isMobile ? "13px" : isSmallScreen ? "18px" : "24px",
            fontWeight: 700,
            fontFamily: "'Poppins', sans-serif",
            color: "#1D47A4",
            marginBottom: isMobile ? "6px" : isSmallScreen ? "7px" : "8px", // TODO: Add tablet value if needed
            lineHeight: 1.3,
          }}
        >
          Targeted marketing (Prevent leakage)
        </Typography>
        <Typography
          sx={{
            fontSize: isVerySmallMobile ? "8px" : isMobile ? "9px" : isSmallScreen ? "12px" : "14px",
            lineHeight: 1.2,
            color: "#4B5563",
            marginBottom: "16px",
          }}
        >
          Increase your lifetime value from the patient by turning your data into patient insights. Each patient has a unique avatar for your marketing campaign
        </Typography>
        <Button
          variant="contained"
          sx={{
            padding: isMobile ? "5px 5px 5px 10px" : isSmallScreen ? "6px 6px 6px 12px" : "8px 8px 8px 16px",
            backgroundColor: "#3567D6",
            textTransform: "none",
            fontSize: isMobile ? "10px" : isSmallScreen ? "11px" : "14px",
            fontWeight: 600,
            borderRadius: isMobile ? "6px" : isSmallScreen ? "7px" : "8px", // TODO: Add tablet value if needed
            "&:hover": {
              backgroundColor: "#2557C6",
            },
          }}
          onClick={() => navigate("/engagement")}
        >
          View Analytics
          <ArrowForwardIcon
            sx={{
              borderRadius: isMobile ? "3px" : isSmallScreen ? "4px" : "8px",
              background: "white",
              fill: "#3567D6",
              width: isMobile ? "16px" : isSmallScreen ? "18px" : "32px",
              height: isMobile ? "16px" : isSmallScreen ? "18px" : "32px",
              marginLeft: isMobile ? "5px" : isSmallScreen ? "6px" : "8px",
            }}
          />
        </Button>
      </Box>
    </Box>
  );
};

